import {
  useV2CreateResourceMutation,
  useV2UpdateResourceMutation,
  useV2DeleteResourceMutation,
} from "@generated";
import type { V2ResourceResource } from "@generated";
import type { ResourceFormValues } from "../types";

// Build the JSON:API `data` body for create/update. The required `account`
// relationship is inferred from auth server-side, so we omit it and cast (same
// pattern as the Currencies MSPA). The API's `user-defined-fields=` setter only
// reads each entry's name + value, so that's all we send back.
export const buildResourceData = (
  values: ResourceFormValues,
  id?: string
): V2ResourceResource => {
  return {
    type: "resources",
    ...(id ? { id: Number(id) } : {}),
    attributes: {
      name: values.name,
      "external-name": values.externalName,
      description: values.description,
      default: values.default,
      "user-defined-fields": values.userDefinedFields.map((f) => ({
        name: f.name,
        value: f.value ?? "",
      })),
    },
  } as unknown as V2ResourceResource;
};

export const useResourceMutations = () => {
  const [create, createState] = useV2CreateResourceMutation();
  const [update, updateState] = useV2UpdateResourceMutation();
  const [destroy, destroyState] = useV2DeleteResourceMutation();

  return {
    create: (values: ResourceFormValues) =>
      create({ body: { data: buildResourceData(values) } }).unwrap(),

    update: (id: string, values: ResourceFormValues) =>
      update({
        id: Number(id),
        body: { data: buildResourceData(values, id) },
      }).unwrap(),

    // Archive (soft-delete: the API stamps deleted_at; restore re-activates).
    archive: (id: string) => destroy({ id: Number(id) }).unwrap(),

    // `active` is not in the generated V2ResourceResource schema; the cast lets
    // restore re-activate a soft-deleted resource (same pattern as Currencies).
    restore: (id: string, name: string) =>
      update({
        id: Number(id),
        body: {
          data: {
            type: "resources",
            id: Number(id),
            attributes: { name, active: true },
          } as unknown as V2ResourceResource,
        },
      }).unwrap(),

    states: { createState, updateState, destroyState },
  };
};

export default useResourceMutations;
